import { useEffect, useMemo, useState } from 'react';

import { parseUniqueId } from '@/typings/parseUniqueId';
import { ResponseAppliedLineageNode } from './discovery/appliedLineage';
import { useFuse } from './fuse';

export interface SearchNode {
  name?: string | null;
  dbtCloudProject?: string | null;
  dbtCoreProject?: string | null;
  hasAccess: boolean;
  group?: string | null;
}

export type PaginationType = 'loadMore' | 'pageBased';

interface SearchParams<TNode extends SearchNode> {
  query: string;
  nodes: TNode[];
  pageSize: number;
  currentPage?: number;
  paginationType?: PaginationType;
}

export type SearchResultData = ReturnType<typeof _getSearchResult>;

const _getSearchResult = (node: ResponseAppliedLineageNode) => {
  const uniqueId = parseUniqueId(node.uniqueId);
  const { raw, ...rest } = uniqueId;
  return {
    ...node,
    ...rest,
    uniqueId: raw,
    name: uniqueId.remainder,
  };
};

const useFuzzySearch = <TNode extends SearchNode>({
  query,
  nodes,
  pageSize,
  currentPage = 0,
  paginationType = 'loadMore',
  searchKeys,
}: SearchParams<TNode> & { searchKeys: any[] }) => {
  // for load more pagination
  const [numResults, updateNumResults] = useState(pageSize);

  useEffect(() => {
    updateNumResults(pageSize);
  }, [pageSize, query]);

  const { hits, setQuery } = useFuse(nodes || [], {
    matchAllOnEmptyQuery: true,
    findAllMatches: true,
    includeScore: true,
    limit: -1,
    ignoreLocation: true,
    distance: 2,
    threshold: 0,
    useExtendedSearch: true,
    keys: searchKeys,
  });

  /**
   * https://fusejs.io/examples.html#extended-search
   */
  useEffect(() => {
    if (!query.trim()) {
      setQuery('');
      return;
    }
    const individualTerms = query
      .split(' ')
      .map((term) => `'${term}`)
      .join(' ');
    const extendedQuery = `${individualTerms}|${query}`;
    setQuery(extendedQuery);
  }, [query, setQuery]);

  return useMemo(() => {
    const allData = hits.map(({ item }) => item);
    const totalCount = allData.length;
    const totalPages = Math.ceil(totalCount / pageSize);
    const fetchMore = () => updateNumResults((value) => value + pageSize);

    if (paginationType === 'loadMore') {
      const data = allData.slice(0, numResults);
      return {
        data,
        totalCount,
        hasMoreResults: numResults < totalCount,
        fetchMore,
        totalPages,
      };
    }
    // For page-based pagination
    const startIndex = currentPage * pageSize;
    const endIndex = Math.min(startIndex + pageSize, totalCount);
    const paginatedData = allData.slice(startIndex, endIndex);

    return {
      data: paginatedData,
      totalCount,
      hasMoreResults: endIndex < totalCount,
      currentPage,
      totalPages,
      fetchMore,
    };
  }, [hits, pageSize, currentPage, numResults, paginationType]);
};

export const useAccountLevelSearchResults = <TNode extends SearchNode>(
  params: SearchParams<TNode>,
) => {
  const accountLevelSearchKeys = [
    {
      name: 'name', // for public models
      weight: 8,
    },
    {
      name: 'dbtCloudProject', // for projects
      weight: 8,
    },
    {
      name: 'dbtCoreProject', // for public models
      weight: 6,
    },
  ];

  return useFuzzySearch({
    ...params,
    searchKeys: accountLevelSearchKeys,
  });
};

export const useProjectLevelSearchResults = <TNode extends SearchNode>(
  params: SearchParams<TNode>,
) => {
  const projectLevelSearchKeys = [
    {
      name: 'name', // for marts
      weight: 8,
    },
  ];

  return useFuzzySearch({
    ...params,
    searchKeys: projectLevelSearchKeys,
  });
};
